import React, {
  createContext,
  FC,
  ReactNode,
  useContext,
  useEffect,
  useState,
} from 'react'
import { Card, Modal, Nav } from 'react-bootstrap'
import { LinkContainer } from 'react-router-bootstrap'
import {
  AISlotModel,
  fetchAiSlotById,
  selectAiSlotById,
} from '@store/slices/aiSlot.slice'
import { Route, Routes, useNavigate, useParams } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { AppDispatch, RootState } from '@store/store'
import {
  ArrowsAngleContract,
  ArrowsAngleExpand,
  CloudUpload,
  Floppy2,
  HouseAdd,
  Trash,
  XCircleFill,
} from 'react-bootstrap-icons'
import AppButton from '@components/shared/appButton'
import { EntityId, unwrapResult } from '@reduxjs/toolkit'
import {
  RegionModel,
  selectAllRegions,
  selectRegionById,
} from '@store/slices/region.slice'
import { useTheme } from '@config/theme.config'
import {
  deleteNarration,
  NarrationModel,
  selectIsNarrationIdInProgress,
  selectNarrationsByIds,
} from '@store/slices/narration.slice'
import { updateNarration } from '@store/thunk/narration.thunk'
import { createNarrationRegions } from '@store/thunk/aiSlot.thunk'
import { selectNarrationBySlotAndRegion } from '@store/selectors/narration.selectors'
import {
  areTranscriptionsEqual,
  handlePublishNarration,
  isNarrationUpdated,
} from '@services/narration.service'
import NarrationFormComponent from '@components/narration/narrationForm.component'
import ConsentModal from '@components/shared/consentModal.component'
import { AudioSourceType } from '@shared/enums'

interface NarrationContextProps {
  narrationInEditing: NarrationModel | undefined
  setNarrationInEditing: React.Dispatch<
    React.SetStateAction<NarrationModel | undefined>
  >
  showModal: boolean
  setShowModal: React.Dispatch<React.SetStateAction<boolean>>
  dialogNumber: number
  setDialogNumber: React.Dispatch<React.SetStateAction<number>>
  isSaveDisabled: () => boolean
  updateNarrationInEditing: () => void
  source: AudioSourceType | undefined
  setSource: (_: AudioSourceType | undefined) => void
}

const NarrationContext = createContext<NarrationContextProps | undefined>(
  undefined
)

export const useNarrationFormContext = (): NarrationContextProps => {
  const context = useContext(NarrationContext)
  if (!context) {
    throw new Error('useNarration must be used within a NarrationProvider')
  }
  return context
}

export const NarrationProvider: FC<{ children: ReactNode }> = ({
  children,
}) => {
  const dispatch = useDispatch<AppDispatch>()
  const { regionId, slotId, playlistId } = useParams()
  const aiSlot: AISlotModel | null = useSelector((state: RootState) =>
    slotId ? selectAiSlotById(state, slotId) : null
  )
  const [narrationInEditing, setNarrationInEditing] = useState<
    NarrationModel | undefined
  >(undefined)
  const [showModal, setShowModal] = useState<boolean>(false)
  const currentNarration: NarrationModel | undefined = useSelector(
    (state: RootState) =>
      slotId && regionId
        ? selectNarrationBySlotAndRegion(state, {
            slotId,
            regionId,
          })
        : undefined
  )
  const narrationLoading = useSelector((state: RootState) =>
    currentNarration
      ? selectIsNarrationIdInProgress(state, currentNarration.id)
      : null
  )
  const isSaveDisabled = (): boolean => {
    return (
      !narrationInEditing ||
      (currentNarration?.phonetic == narrationInEditing.phonetic &&
        currentNarration?.text == narrationInEditing.text &&
        currentNarration?.ssml == narrationInEditing.ssml &&
        (!currentNarration.dialogue ||
          areTranscriptionsEqual(
            currentNarration?.dialogue,
            narrationInEditing.dialogue
          )))
    )
  }
  const [dialogNumber, setDialogNumber] = useState<number>(0)
  const navigate = useNavigate()

  //Fetch slot if missing
  useEffect(() => {
    if (slotId && !aiSlot && !narrationLoading)
      dispatch(fetchAiSlotById(slotId))
    setNarrationInEditing(undefined)
  }, [slotId])

  //Set editing narration according to regionId and set Default source
  useEffect(() => {
    if (!source || regionId != narrationInEditing?.region) {
      if (currentNarration?.dialogue) {
        setLinkSource(AudioSourceType.DIALOGUE)
      } else if (currentNarration?.text) {
        setLinkSource(AudioSourceType.TEXT)
      } else if (currentNarration?.ssml) {
        setLinkSource(AudioSourceType.SSML)
      } else {
        setLinkSource(AudioSourceType.TEXT)
      }
    } else if (
      source == AudioSourceType.DIALOGUE &&
      !currentNarration?.dialogue
    ) {
      setLinkSource(undefined)
    }
  }, [currentNarration, regionId])

  useEffect(() => {
    if (currentNarration) {
      updateNarrationInEditing()
    }
  }, [currentNarration])

  const updateNarrationInEditing = () => {
    if (narrationInEditing && narrationInEditing.id == currentNarration?.id) {
      const updatedNarration = {
        ...currentNarration,
        text:
          narrationInEditing.text && narrationInEditing.text != ''
            ? narrationInEditing.text
            : currentNarration.text,
        phonetic:
          narrationInEditing.phonetic && narrationInEditing.phonetic != ''
            ? narrationInEditing.phonetic
            : currentNarration.phonetic,
        ssml: currentNarration.ssml,
        dialogue:
          narrationInEditing.dialogue &&
          narrationInEditing.dialogue.sessions.length == 0
            ? narrationInEditing.dialogue
            : currentNarration.dialogue,
      }
      setNarrationInEditing(updatedNarration)
    } else {
      setNarrationInEditing(currentNarration)
    }
  }

  const [source, setSource] = useState<AudioSourceType>()

  const setLinkSource = (source: AudioSourceType | undefined) => {
    setSource(source)
    if (source && regionId) {
      navigate(
        `/playlist/${playlistId}/slot/${slotId}/region/${regionId}/source/${source.toString().toLowerCase()}`
      )
    } else if (regionId) {
      navigate(`/playlist/${playlistId}/slot/${slotId}/region/${regionId}`)
    }
  }

  return (
    <NarrationContext.Provider
      value={{
        narrationInEditing,
        setNarrationInEditing,
        showModal,
        setShowModal,
        dialogNumber,
        setDialogNumber,
        isSaveDisabled,
        updateNarrationInEditing,
        source,
        setSource: setLinkSource,
      }}
    >
      {children}
    </NarrationContext.Provider>
  )
}

const NarrationComponent: React.FC = () => (
  <div className={'d-flex gap-2 my-2'}>
    <NarrationProvider>
      <Routes>
        <Route
          path="/source/:sourceType"
          element={<NarrationMabyModalComponent />}
        />
        <Route path="" element={<NarrationMabyModalComponent />} />
      </Routes>
    </NarrationProvider>
  </div>
)

const NarrationMabyModalComponent: FC = () => {
  const { theme } = useTheme()
  const { showModal } = useNarrationFormContext()
  return (
    <>
      <Narration />
      {showModal && (
        <Modal
          data-bs-theme={theme}
          className="news-editor-modal"
          show={showModal}
          backdrop="static"
          size={'lg'}
        >
          <div
            className={'w-100 m-auto h-100 p-2'}
            style={{ maxWidth: '800px' }}
          >
            <Narration />
          </div>
        </Modal>
      )}
    </>
  )
}

const Narration: FC = () => {
  const { regionId, slotId, playlistId } = useParams()
  const { narrationInEditing } = useNarrationFormContext()
  const dispatch = useDispatch<AppDispatch>()
  const aiSlot: AISlotModel | null = useSelector((state: RootState) =>
    slotId ? selectAiSlotById(state, slotId) : null
  )

  const slotNarrations: NarrationModel[] = useSelector((state: RootState) =>
    aiSlot ? selectNarrationsByIds(state, aiSlot.narrations) : []
  )
  const currentNarration: NarrationModel | undefined = useSelector(
    (state: RootState) =>
      slotId && regionId
        ? selectNarrationBySlotAndRegion(state, {
            slotId,
            regionId,
          })
        : undefined
  )
  const regions: RegionModel[] = useSelector(selectAllRegions)
  const region: RegionModel | null = useSelector((state: RootState) =>
    regionId ? selectRegionById(state, regionId) : null
  )

  const AddRegionButton: FC = () => {
    const onAddRegions = () => {
      if (slotId) return dispatch(createNarrationRegions({ slotId }))
    }

    return (
      <AppButton
        onClick={() => onAddRegions()}
        buttonChild={<HouseAdd size={24} />}
        isLoading={false}
        buttonVariant={'primary'}
        className={'me-1'}
        tooltip={'Přidat chybějící regiony'}
      />
    )
  }

  const RegionSelect: FC = () => {
    const RegionNavLink: FC<{
      regionId: EntityId
      narratedText: NarrationModel
    }> = ({ regionId, narratedText }) => {
      const { theme } = useTheme()
      const { isSaveDisabled } = useNarrationFormContext()
      const link = `/playlist/${playlistId}/slot/${slotId}/region/${regionId}`
      const region = regions.find((region) => region.id === regionId)
      const linkClasses = narratedText.audioCreated
        ? narratedText.uploaded &&
          new Date(narratedText.uploaded) > new Date(narratedText.audioCreated)
          ? 'text-success'
          : 'text-primary'
        : theme === 'dark'
          ? 'text-white'
          : 'text-dark'

      const [showModal, setShowModal] = useState(false)
      const navigate = useNavigate()

      const handleConfirmNavigation = () => {
        setShowModal(false)
        navigate(link)
      }

      const handleNavLinkClick = (e: React.MouseEvent) => {
        e.preventDefault()
        if (isSaveDisabled()) {
          navigate(link)
        } else {
          setShowModal(true)
        }
      }

      return (
        <>
          <Nav.Item key={regionId} aria-disabled={!regionId}>
            <LinkContainer to={link} onClick={handleNavLinkClick}>
              <Nav.Link
                className={linkClasses + ' fw-bold'}
                eventKey={regionId}
              >
                {region?.name}
              </Nav.Link>
            </LinkContainer>
          </Nav.Item>
          <ConsentModal
            show={showModal}
            onBack={() => setShowModal(false)}
            onHide={() => setShowModal(false)}
            onAgree={handleConfirmNavigation}
            header="Máte neuloženou práci"
            body="V tomto regionu máte neuloženou práci. I tak ho chcete opustit?"
          />
        </>
      )
    }

    return (
      <div className={'d-flex gap-2 align-items-center'}>
        {aiSlot && aiSlot?.narrations && aiSlot.narrations?.length > 0 ? (
          <Nav variant="tabs" activeKey={regionId}>
            {aiSlot.narrations.length < regions.length && (
              <AddRegionButton></AddRegionButton>
            )}
            {slotNarrations
              .filter(
                (narration: NarrationModel) =>
                  regions.find(
                    (region: RegionModel) => region.id == narration.region
                  )?.isDefault
              )
              .sort(
                (a: NarrationModel, b: NarrationModel) =>
                  Number.parseInt(a.id as string) -
                  Number.parseInt(b.id as string)
              )
              .map((narration) => (
                <RegionNavLink
                  key={aiSlot?.id.toString() + narration.region.toString()}
                  regionId={narration.region}
                  narratedText={narration}
                />
              ))}
            {slotNarrations
              .filter(
                (narration: NarrationModel) =>
                  !regions.find(
                    (region: RegionModel) => region.id == narration.region
                  )?.isDefault
              )
              .map((narration) => (
                <RegionNavLink
                  key={aiSlot?.id.toString() + narration.region.toString()}
                  regionId={narration.region}
                  narratedText={narration}
                />
              ))}
          </Nav>
        ) : (
          <div className={'d-flex gap-2 align-items-center'}>
            <strong>Regiony ještě nejsou vytvořeny</strong>
            <AddRegionButton></AddRegionButton>
          </div>
        )}
      </div>
    )
  }

  return (
    <div className={'w-100'}>
      <RegionSelect />
      {currentNarration && (
        <Card>
          <Card.Header>
            <div className={'d-flex gap-2 justify-content-between'}>
              <h4 className="p-0 m-0">{region?.name.toUpperCase()}</h4>
              <NarrationButtons />
            </div>
          </Card.Header>
          <Card.Body>
            {narrationInEditing && <NarrationFormComponent />}
          </Card.Body>
        </Card>
      )}
    </div>
  )
}

const NarrationButtons: FC = () => {
  const { regionId, slotId, playlistId } = useParams()
  const {
    narrationInEditing,
    setNarrationInEditing,
    showModal,
    setShowModal,
    isSaveDisabled,
  } = useNarrationFormContext()
  const dispatch = useDispatch<AppDispatch>()
  const navigate = useNavigate()
  const currentNarration: NarrationModel | undefined = useSelector(
    (state: RootState) =>
      slotId && regionId
        ? selectNarrationBySlotAndRegion(state, {
            slotId,
            regionId,
          })
        : undefined
  )
  const narrationLoading = useSelector((state: RootState) =>
    currentNarration
      ? selectIsNarrationIdInProgress(state, currentNarration.id)
      : null
  )
  const aiSlot: AISlotModel | null = useSelector((state: RootState) =>
    slotId ? selectAiSlotById(state, slotId) : null
  )
  const regions: RegionModel[] = useSelector(selectAllRegions)
  const region = useSelector((state: RootState) =>
    regionId ? selectRegionById(state, regionId) : null
  )
  const onPublishAudioClick = () => {
    if (currentNarration && playlistId)
      return handlePublishNarration(currentNarration, playlistId)
  }
  const [showConsentModal, setShowConsentModal] = useState(false)

  const onDeleteNarrationClick = (e: React.MouseEvent) => {
    e.preventDefault()
    setShowConsentModal(true)
  }

  const dispatchDeleteNarration = () => {
    if (currentNarration)
      return dispatch(deleteNarration(currentNarration.id))
        .then(unwrapResult)
        .then(() => {
          aiSlot && dispatch(fetchAiSlotById(aiSlot.id))
          setNarrationInEditing(undefined)
          navigate(`/playlist/${playlistId}/slot/${slotId}`)
        })
    setShowConsentModal(false)
  }

  const onResetNarrationClick = () => {
    if (currentNarration) setNarrationInEditing(currentNarration)
  }
  const onNarrationUpdate = () => {
    if (currentNarration && narrationInEditing) {
      const data = {
        text: narrationInEditing.text,
        phonetic: narrationInEditing.phonetic,
        ssml: narrationInEditing.ssml,
        dialogue: narrationInEditing.dialogue,
      }
      return dispatch(
        updateNarration({ narrationId: currentNarration.id, ...data })
      )
    }
  }

  return (
    <div className="d-flex gap-2 justify-content-end">
      <AppButton
        buttonVariant={'secondary'}
        tooltip={'Otevřít v okně'}
        buttonChild={
          showModal ? <ArrowsAngleContract /> : <ArrowsAngleExpand />
        }
        onClick={() => setShowModal(!showModal)}
      ></AppButton>
      <AppButton
        isLoading={narrationLoading}
        buttonVariant={'danger'}
        tooltip={'Smazat region'}
        buttonChild={<Trash />}
        onClick={onDeleteNarrationClick}
        disabled={
          !regionId ||
          regions.find((region) => region.id == regionId)?.isDefault
        }
      ></AppButton>
      <AppButton
        isLoading={narrationLoading}
        buttonVariant={'warning'}
        tooltip={'Vrátit změny'}
        buttonChild={<XCircleFill />}
        onClick={onResetNarrationClick}
        disabled={isSaveDisabled()}
      ></AppButton>
      <AppButton
        isLoading={narrationLoading}
        buttonVariant={'primary'}
        tooltip={'Uložit'}
        onClick={onNarrationUpdate}
        disabled={isSaveDisabled()}
        buttonChild={<Floppy2 />}
      ></AppButton>
      <AppButton
        isLoading={narrationLoading}
        buttonVariant={'success'}
        tooltip={'Publikovat region'}
        buttonChild={<CloudUpload />}
        onClick={onPublishAudioClick}
        disabled={
          !isSaveDisabled() ||
          !currentNarration?.audioUrl ||
          !isNarrationUpdated(currentNarration)
        }
      ></AppButton>
      <ConsentModal
        show={showConsentModal}
        header={'Smazání regionu'}
        body={`Opravdu chcete smazat region ${region?.name}?`}
        onAgree={dispatchDeleteNarration}
        onBack={() => setShowConsentModal(false)}
        onHide={() => setShowConsentModal(false)}
      />
    </div>
  )
}

export default NarrationComponent
